import { Injectable } from '@angular/core';
import { BehaviorSubject } from 'rxjs';
import { ActionData, ActionState, ActionStateInfo, ACUpdateRequest, ActionLocationIdentifier } from '../types/action-center-types';
import { ActionCenterModel } from '../types/action-center-model';
import { PageStateModel } from '../types/page-state-model';
import { ActionCenterRequests } from 'src/app/core/services/action-center-requests.service';
import { Guid } from 'guid-typescript';
import { AlertService } from 'src/app/core/services/alert.service';
import { cloneDeep } from 'lodash';

@Injectable({
  providedIn: 'root'
})
export class ActionCenterService {

  // Tracks the current page state
  pageState: BehaviorSubject<ActionState> = new BehaviorSubject<ActionState>(ActionState.Recommended);

  // Stores the action center data
  acSource: BehaviorSubject<ActionCenterModel> = new BehaviorSubject<ActionCenterModel>(new ActionCenterModel());
  acObservable = this.acSource.asObservable();

  // Stores the Page State Data to be displayed
  pageData: BehaviorSubject<PageStateModel> = new BehaviorSubject<PageStateModel>(new PageStateModel());
  pageDataObservable = this.pageData.asObservable();

  constructor(private acRequests: ActionCenterRequests, private _alertService: AlertService) { }

  public resetObservablesForActionCenter() {
    this.pageState.next(ActionState.Recommended);
    this.acSource.next(new ActionCenterModel());
    this.pageData.next(new PageStateModel());
  }

  /**
   * Sets the page Data for the current page state.
   * This is done by fetching the data from acSource, and setting the pageData.
   * If the data is not available, the data is fetched from the API.
   * Or, if the page data needs to be updated, the page data should be updated first, by syncing with API.
   */
  async updateContentForPageState() {
    const pageState = this.pageState.value;
    // fetch acSource
    let pageData = this.getPageDataForActionState(pageState);
    if (pageData === undefined || pageData.isEmpty() || pageData.isUpdateNeeded) {
      pageData = await this._fetchPageData(pageState);

      if (pageState === ActionState.MyActions || pageState === ActionState.Recommended)
        pageData.calculateTVDOpportunity();

      // set the acSource
      this.setPageDataForActionState(pageState, pageData);
    }

    pageData.isUpdateNeeded = false;
    this.pageData.next(pageData);
  }

  /**
   * Fetches the page data for the given page state.
   * The data is fetched from the API => getRecommendations.
   * If page state is my actions or recommended, the TVD opportunity is also calculated.
   * @param pageState The page state for which the data is to be fetched.
   */
  public async _fetchPageData(pageState: ActionState): Promise<PageStateModel> {
    let pageData: PageStateModel = new PageStateModel();
    pageData.actions = await this.getRecommendations(pageState);
    return pageData;
  }

  //#region Moving Actions to Different States
  public async moveActionToDifferentState(recId: string, currState: ActionState, newState: ActionState, additionalInfo: any) {
    try {
      // Get current page model
      let currPageData = cloneDeep(this.getPageDataForActionState(currState));
      if (currPageData === undefined || currPageData.isEmpty()) throw new Error(`Page data for ${currState} not found`);

      // Get the actions from the current state
      let actions = currPageData?.actions;
      // Get the action to be moved
      let action = cloneDeep(actions?.find(action => action.recId === recId));

      // Check if the action is found
      if (action === undefined) throw new Error(`Action with recId ${recId} not found in ${currState}`);

      // Update the state info
      this.updateStateInfo(action!.recState, newState, additionalInfo);

      // Build the update request payload
      let request = this.buildUpdateRequestPayload(action!);

      // Move the action to the new state - API call
      let status = await this.moveToNewState(request);

      if (status == 200) {
        // Remove the action from the current state
        currPageData.actions = actions.filter(action => action.recId !== recId);
        currPageData.isUpdateNeeded = true;

        if (currState === ActionState.MyActions || currState === ActionState.Recommended)
          currPageData.calculateTVDOpportunity();

        // Set the updated page data
        this.setPageDataForActionState(currState, currPageData);
        this.pageData.next(currPageData);

        let newPageData = cloneDeep(this.getPageDataForActionState(newState));
        if (newPageData != undefined) newPageData.isUpdateNeeded = true;
        this.setPageDataForActionState(newState, newPageData);
        
        return true;
      }
      return false;
    }
    catch (error) {
      this._handleError("Error while updating state for the recommendation", error);
      return false;
    }
  }

  public buildUpdateRequestPayload(action: ActionData): ACUpdateRequest {
    let updateRequest: ACUpdateRequest = {
      recId: action.recId,
      locationIds: action.currentLocations.map(location => ({ key: location.key, accountNumber: location.accountNumber })),
      solution: action.solution,
      productName: action.productName,
      state: action.recState,
    };

    return updateRequest;
  }

  public updateStateInfo(stateInfo: ActionStateInfo, newState: ActionState, additionalInfo: any) {
    try {
      switch (newState) {
        case ActionState.MyActions:
          stateInfo.startDate = new Date(Date.now()).toUTCString();
          break;
        case ActionState.Completed:
          stateInfo.endDate = new Date(Date.now()).toUTCString();
          break;
        case ActionState.Dismissed:
          if (!additionalInfo['dismissalReason']) throw new Error('Dismissal Reason is required');
          stateInfo.dismissReason = additionalInfo['dismissalReason'];
          break;
        case ActionState.Snoozed:
          if (!additionalInfo['snoozeDuration']) throw new Error('Snooze Duration is required');
          stateInfo.snoozeDuration = additionalInfo['snoozeDuration'];
          stateInfo.snoozeDate = new Date(Date.now()).toUTCString();
          break;
        default:
          throw new Error('Invalid state');
      }
      stateInfo.recommendationState = newState;
    }
    catch (error) {
      this._handleError('Error while updating state info for the recommendation', error);
    }
  }
  //#endregion

  //#region API Calls
  async getRecommendations(recState: string) {
    try {
      const { data } = await this.acRequests.getRecommendations(recState);
      return data;
    }
    catch (error) {
      this._handleError(`Retrieving recommendations for ${recState} failed`, error);
      return [];
    }
  }

  async moveToNewState(acRequest: ACUpdateRequest) {

    try {
      const { status } = await this.acRequests.moveToNewState(acRequest);
      return status;
    }
    catch (error) {
      this._handleError(`Updating to ${acRequest.state} failed`, error);
      return [];
    }
  }
  //#endregion

  //#region Getters and Setters - acSource
  public getPageDataForActionState(pageState: ActionState): PageStateModel | undefined {
    let pageData: PageStateModel | undefined;
    let acData = this.acSource.value;

    switch (pageState) {
      case ActionState.MyActions:
        pageData = acData.MyActions;
        break;
      case ActionState.Recommended:
        pageData = acData.Recommended;
        break;
      case ActionState.Completed:
        pageData = acData.Completed;
        break;
      case ActionState.Dismissed:
        pageData = acData.Dismissed;
        break;
      case ActionState.Snoozed:
        pageData = acData.Snoozed;
        break;
    }
    return pageData;
  }

  public setPageDataForActionState(pageState: ActionState, pageData: PageStateModel | undefined) {
    let acData = this.acSource.value;

    switch (pageState) {
      case ActionState.MyActions:
        acData.MyActions = pageData;
        break;
      case ActionState.Recommended:
        acData.Recommended = pageData;
        break;
      case ActionState.Completed:
        acData.Completed = pageData;
        break;
      case ActionState.Dismissed:
        acData.Dismissed = pageData;
        break;
      case ActionState.Snoozed:
        acData.Snoozed = pageData;
        break;
    }

    this.acSource.next(acData);
  }
  //#endregion

  //#region Helper Functions
  public _handleError(errorMessage: string, error: any) {
    this._alertService.addAlert({
      message: `${errorMessage} If problem persists, please report a bug.`,
      id: Guid.create()
    });
  }

  /**
   * Create a list of solutions for advanced filters based on data available on current page and tab selected.
   * @returns List of solutions.
   */
  public _getsolutionNames(): string[] {
    const solutionSet = new Set<string>();
    let pageDataValues = this.pageData.value;
    if(pageDataValues && pageDataValues.actions) {
      pageDataValues.actions.forEach(action => {
        solutionSet.add(this._toTitleCase(action.solution));
      });
    }
    return Array.from(solutionSet);
  }

  private _toTitleCase(str: string): string {
    return str
        .toLowerCase()
        .replace(/\b(\w)/g, char => char.toUpperCase())
        .replace(/\bWw\b/g, 'WW'); // Special case for "WW"
  }
  //#endregion
}
